import React, { useEffect, useState } from "react";
import { Save, UserRound } from "lucide-react";
import { toast } from "sonner";
import MediaPicker from "@/components/MediaPicker";
import { api, formatError } from "@/lib/api";
import { showAdminFieldErrors } from "@/components/admin/adminValidation";

export const AdminProfileTab = ({ user, onUserUpdated }) => {
  const [name, setName] = useState(user?.name || "");
  const [picture, setPicture] = useState(user?.picture || "");
  const [saving, setSaving] = useState(false);
  const [mediaBusy, setMediaBusy] = useState(false);

  useEffect(() => {
    setName(user?.name || "");
    setPicture(user?.picture || "");
  }, [user]);

  const saveProfile = async (event) => {
    event.preventDefault();
    if (!name.trim()) {
      showAdminFieldErrors("Full name is required", ["admin-profile-name-input"]);
      return;
    }
    setSaving(true);
    try {
      const response = await api.put("/admin/profile", {
        name: name.trim(),
        picture: picture || null,
      });
      onUserUpdated(response.data);
      toast.success("Admin profile saved");
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setSaving(false);
    }
  };

  const initials = (name || "Admin")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  return (
    <div className="max-w-3xl" data-testid="admin-profile-tab">
      <div className="border-b border-[#E2DCD2] pb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#8C6D46]">
          Account settings
        </p>
        <h2 className="mt-2 font-serif text-3xl font-semibold text-[#1E2A24]">My Profile</h2>
        <p className="mt-2 text-sm text-[#64748B]">
          Update the identity displayed in your Admin sidebar.
        </p>
      </div>

      <form onSubmit={saveProfile} className="mt-6 space-y-6">
        <div className="border border-[#E2DCD2] bg-white p-6 shadow-[0_8px_24px_rgba(45,90,71,0.04)]">
          <div className="flex flex-wrap items-center gap-4">
            {picture ? (
              <img
                src={picture}
                alt="Admin profile"
                className="h-16 w-16 rounded-full border border-[#E2DCD2] object-cover"
                data-testid="admin-profile-avatar-preview"
              />
            ) : (
              <div
                className="flex h-16 w-16 items-center justify-center rounded-full bg-[#EBF2EE] font-serif text-xl text-[#2D5A47]"
                data-testid="admin-profile-avatar-placeholder"
              >
                {initials || <UserRound size={22} />}
              </div>
            )}
            <div>
              <h3 className="font-semibold text-[#1E2A24]">Profile picture</h3>
              <p className="mt-1 text-sm text-[#64748B]">Choose one image from the existing media library.</p>
            </div>
          </div>
          <div className="mt-5">
            <MediaPicker
              images={picture ? [picture] : []}
              setImages={(images) => setPicture(images[0] || "")}
              maxImages={1}
              allowVideo={false}
              imageHint="Square image · JPG/PNG/WebP · ≤ 8 MB"
              testId="admin-profile-picture"
              onBusyChange={setMediaBusy}
            />
          </div>
        </div>

        <div className="border border-[#E2DCD2] bg-white p-6 shadow-[0_8px_24px_rgba(45,90,71,0.04)]">
          <label className="block text-xs font-semibold uppercase tracking-wider text-[#8C6D46]">
            Full Name
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Admin name"
              className="mt-2 block w-full border border-[#E2DCD2] px-3 py-2.5 text-sm text-[#1E2A24] outline-none focus:border-[#2D5A47]"
              data-testid="admin-profile-name-input"
            />
          </label>
          <p className="mt-2 text-xs text-[#64748B]">Email remains managed by your sign-in account.</p>
        </div>

        <button
          type="submit"
          disabled={saving || mediaBusy}
          className="inline-flex items-center gap-2 bg-[#2D5A47] px-5 py-3 text-sm font-semibold text-white hover:bg-[#214435] disabled:opacity-50"
          data-testid="save-admin-profile"
        >
          <Save size={15} /> {saving ? "Saving…" : mediaBusy ? "Uploading image…" : "Save Profile"}
        </button>
      </form>
    </div>
  );
};